import React from 'react'
import { ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native'
import { ACSharedStyles } from '../../admin-config-common'
import { useTranslation } from 'react-i18next'
import {
  AppContext,
  assets,
  HightLightTable,
  HightLightTableColum,
  Input,
  Permission,
  Select,
  sharedStyle
} from '@pulseops/common'
import { makeStyles, Tooltip, Typography } from '@material-ui/core'
import { ACViewMetricListService } from '../../admin-config-service'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from '@mxt/zio'
import { useLoading } from '@mxt/zio-react'
import { StackScreenProps } from '@react-navigation/stack'
import { AdministratorConfigStackParamList } from '../../AdministrationConfigStackParamList'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { ACViewMetricListForm } from './ACViewMetricListForm'
import { ACEditMetricListConst } from './ACEditMetricListConst'
import AntDesign from '@expo/vector-icons/AntDesign'
import * as O from 'fp-ts/lib/Option'
import { AdministrationConfigContext } from '../../AdministrationConfigContext'

export const ACEditMetricList = (props: StackScreenProps<AdministratorConfigStackParamList, 'ACEditMetricList'>) => {
  const { t, i18n } = useTranslation()
  const { height, width } = useWindowDimensions()
  const [isLoading, bindLoader] = useLoading(false)
  const isFocused = useIsFocused()
  const { changeBreadcrumb, showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const [editingMetricList, setEditingMetricList] = React.useState<ACViewMetricListService.ACViewMetricResponse[]>([])
  const { userPermissions } = React.useContext(AdministrationConfigContext)
  const rootNavigation = useNavigation()
  const groupId = props.route.params.groupId
  const makeTableStyles = makeStyles({
    container: {
      border: `1px solid #D3DCE6`,
      borderWidth: 1,
      borderRadius: 8,
      maxHeight: height - 343,
      maxWidth: width - 40
    }
  })
  const { control, setValue, getValues, watch, trigger } = useForm<ACViewMetricListForm.ACEditMetricField>({
    defaultValues: {
      editingMetricArr: []
    }
  })

  const metricFieldArray = useFieldArray<ACViewMetricListForm.ACEditMetricField>({
    control: control,
    name: 'editingMetricArr'
  })

  const displayedColumns: HightLightTableColum[] = [
    {
      title: t('AdministratorConfig:GroupMetrics'),
      fieldName: 'groupMetrics',
      disabled: true
    },
    {
      title: t('AdministratorConfig:MetricName'),
      fieldName: 'metricName',
      disabled: true
    },
    {
      title: t('AdministratorConfig:Value'),
      fieldName: 'value',
      disabled: true
    },
    {
      title: t('AdministratorConfig:Description'),
      fieldName: 'description',
      disabled: true
    },
    {
      title: t('AdministratorConfig:Operator'),
      fieldName: 'operator',
      disabled: true,
      rowRender: (selectedProp) => {
        return (
          <Controller
            control={control}
            name={`editingMetricArr.${selectedProp.index}.operator`}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <Select
                options={ACEditMetricListConst.OperatorConst}
                value={value}
                onChange={(val) => {
                  onChange(val)
                }}
                popupIcon={<assets.ArrowDownDropdownIcon />}
                placeholder={t('AdministratorConfig:Select')}
              />
            )}
          />
        )
      }
    },
    {
      title: t('AdministratorConfig:OperatorValue'),
      fieldName: 'operatorValue',
      disabled: true,
      rowRender: (selectedProp) => {
        return (
          <Controller
            control={control}
            name={`editingMetricArr.${selectedProp.index}.operatorValue`}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <Input
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                filled
                maxLength={255}
                inputType={isNumber16Group ? 'text' : 'number'}
                // errorMessage={error?.message}
              />
            )}
          />
        )
      }
    },
    {
      title: t('AdministratorConfig:RAGScore'),
      fieldName: 'ragThreshold',
      disabled: true,
      rowRender: (selectedProp) => {
        return (
          <Controller
            control={control}
            name={`editingMetricArr.${selectedProp.index}.ragThreshold`}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <Input
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                filled
                // maxLength={300}
                // errorMessage={error?.message}
              />
            )}
          />
        )
      }
    },
    {
      title: t('AdministratorConfig:Multiplier'),
      fieldName: 'multiplier',
      disabled: true,
      rowRender: (selectedProp) => {
        return (
          <Controller
            control={control}
            name={`editingMetricArr.${selectedProp.index}.multiplier`}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <Input value={value} onChange={onChange} onBlur={onBlur} filled inputType={'number'} />
            )}
          />
        )
      }
    }
  ]

  const getTooltipContent = () => {
    const questionContent =
      i18n.language === 'en'
        ? ACEditMetricListConst.ToolTipConst.questionEn
        : ACEditMetricListConst.ToolTipConst.questionEn
    const content = <Typography component="div" dangerouslySetInnerHTML={{ __html: questionContent }} />
    return content
  }

  React.useEffect(() => {
    showGlobalLoading(isLoading)
  }, [isLoading])

  React.useEffect(() => {
    if (isFocused && groupId) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            rootNavigation.navigate('HomeScreen')
          }
        },
        {
          title: t('AdministratorConfig:MetricsConfiguration'),
          navigate: () => {
            props.navigation.navigate('ACViewMetricList')
          }
        },
        {
          title: t('AdministratorConfig:EditMetrics'),
          navigate: null
        }
      ])
      if (userPermissions && userPermissions.length > 0) {
        if (!userPermissions.includes(Permission.LinkEditMetricBSCAdminConfig)) {
          rootNavigation.navigate('HomeScreen')
        } else {
          getViewMetricList(groupId)
        }
      }
    }
    return () => {
      metricFieldArray.remove()
      setValue('editingMetricArr', [])
      setEditingMetricList([])
    }
  }, [isFocused, groupId, userPermissions])

  const isNumber16Group = React.useMemo(() => {
    return ['G16'].includes(groupId)
  }, [groupId])

  const getViewMetricList = (groupID: string) => {
    const bodyData: ACViewMetricListService.ACViewMetricListRequest = {
      start: 0,
      size: 100,
      orders: [],
      sort: null,
      groupId: groupID,
      metricCode: null,
      visible: true,
      isPaginate: false
    }
    pipe(
      ACViewMetricListService.getACViewMetricList(bodyData),
      ZIO.map((metricResponse) => {
        if (metricResponse.data && metricResponse.data.length > 0) {
          setEditingMetricList(metricResponse.data)
          initialEditingMetricListForm(metricResponse.data)
        } else {
          setEditingMetricList([])
        }
        return metricResponse
      }),
      ZIO.mapError((error) => {
        setEditingMetricList([])
        return error
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const initialEditingMetricListForm = (searchedList: ACViewMetricListService.ACViewMetricResponse[]) => {
    let i = 0
    metricFieldArray.remove()
    setValue('editingMetricArr', [])
    while (i < searchedList.length) {
      const operator = pipe(
        ACEditMetricListConst.OperatorConst.find((x) => x.value === searchedList[i].operator),
        O.fromNullable,
        O.fold(
          () => ({ label: '', value: '' }),
          (operatorItem) => ({ label: operatorItem.value, value: operatorItem.value })
        )
      )
      metricFieldArray.append({
        operator: operator,
        operatorValue: searchedList[i].operatorValue,
        ragThreshold: searchedList[i].ragThreshold,
        multiplier: searchedList[i].multiplier,
        historyID: searchedList[i].id
      })
      setValue(`editingMetricArr.${i}.operator`, operator)
      setValue(`editingMetricArr.${i}.operatorValue`, searchedList[i].operatorValue)
      setValue(`editingMetricArr.${i}.ragThreshold`, searchedList[i].ragThreshold)
      setValue(`editingMetricArr.${i}.multiplier`, searchedList[i].multiplier)
      setValue(`editingMetricArr.${i}.historyID`, searchedList[i].id)
      i++
    }
  }

  const onResetEvent = () => {
    initialEditingMetricListForm(editingMetricList)
  }

  const getSaveData = () => {
    const formData = getValues()
    let savedData: ACViewMetricListService.ACViewMetricResponse[] = []
    savedData = formData.editingMetricArr.map((metricField) => {
      const metricData = editingMetricList.find((x) => x.id === metricField.historyID)
      return {
        id: metricData?.id || '',
        metricCode: metricData?.metricCode || '',
        groupId: metricData?.groupId || '',
        groupMetrics: metricData?.groupMetrics || '',
        metricName: metricData?.metricName || '',
        value: metricData?.value || '',
        method: metricData?.method || '',
        description: metricData?.description || '',
        priority: metricData?.priority || '',
        operator: metricField.operator?.value || '',
        operatorValue: metricField.operatorValue || '',
        type: metricData?.type || '',
        ragThreshold: metricField.ragThreshold || '',
        multiplier: metricField.multiplier || '',
        visible: metricData?.visible || false
      }
    })
    return savedData
  }

  const checkDiffMultiplier = (formData: ACViewMetricListForm.ACEditMetricField) => {
    let isDifferenceValue = false
    for (let i = 0; i < formData.editingMetricArr.length; i++) {
      if (
        formData.editingMetricArr.some(
          (p, index) => index !== i && p.multiplier !== formData.editingMetricArr[i].multiplier
        )
      ) {
        isDifferenceValue = true
        break
      } else {
        isDifferenceValue = false
      }
    }
    return isDifferenceValue
  }

  const checkValidGroupMetricWithAC6Rule = (groupID: string) => {
    return ['G08', 'G09', 'G03', 'G04', 'G11', 'G10'].includes(groupID)
  }

  const validateAC6Rule = () => {
    let isValidFlag = true
    let previousOperatorValue = 0
    const formData = getValues()
    const formMetricList = formData.editingMetricArr
    if (checkValidGroupMetricWithAC6Rule(groupId)) {
      const maxOperatorValue = formMetricList.reduce((total, item) => {
        return Number(item.operatorValue) > total ? Number(item.operatorValue) : total
      }, 0)
      const minOperatorValue = formMetricList.reduce((total, item) => {
        return Number(item.operatorValue) < total || total === 0 ? Number(item.operatorValue) : total
      }, 0)
      const minValIndex = formMetricList.findIndex((x) => Number(x.operatorValue) === minOperatorValue)
      //neu toan hang min nam o dong dau tien nhung toan hang max ko nam o dong cuoi hoac nguoc lai thi bao loi MS020095
      if (
        formMetricList.length > 0 &&
        !(
          Number(formMetricList[0].operatorValue) === minOperatorValue &&
          Number(formMetricList[formMetricList.length - 1].operatorValue) === maxOperatorValue
        ) &&
        !(
          Number(formMetricList[0].operatorValue) === maxOperatorValue &&
          Number(formMetricList[formMetricList.length - 1].operatorValue) === minOperatorValue
        )
      ) {
        showToast(t('message:MS020095'), 'error')
        isValidFlag = false
      } else if (maxOperatorValue > 100) {
        // dang xet gia tri lon nhat cua toan hang chi co 100 (100%) neu hon lon thi bao loi MS020095
        showToast(t('message:MS020095'), 'error')
        isValidFlag = false
      } else if (minValIndex === 0) {
        // toan hang nho nhat o dong dau tien
        if (!['<', '<='].includes(formMetricList[minValIndex].operator?.value || '')) {
          //neu gia tri toan tu dong dau tien khac 2 toan tu '<', '<=' thi sai AC6 rule bao loi MS020095
          showToast(t('message:MS020095'), 'error')
          isValidFlag = false
        } else {
          previousOperatorValue = minOperatorValue
          for (let i = minValIndex + 1; i < formMetricList.length; i++) {
            if (i === formMetricList.length - 1) {
              if (Number(formMetricList[i].operatorValue) < 100) {
                if (!['>', '>='].includes(formMetricList[i].operator?.value || '')) {
                  // neu toan hang dong duoi cung < 100 va khac 2 toan tu '>', '>=' thi bao loi MS020095
                  showToast(t('message:MS020095'), 'error')
                  isValidFlag = false
                  break
                }
              } else if (!['<', '<='].includes(formMetricList[i].operator?.value || '')) {
                // nguoc lai neu toan hang dong duoi cung = 100 va khac 2 toan tu '<', '<=' thi bao loi MS020095
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              }
            } else {
              if (Number(formMetricList[i].operatorValue) <= previousOperatorValue) {
                // neu gia tri toan hang dong tiep theo ma <= toan hang dong dau tien bao loi MS020095
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              } else if (
                formMetricList[i].operator?.value === '<=' &&
                Number(formMetricList[i].operatorValue) === maxOperatorValue
              ) {
                // neu gia tri toan hang dong tiep theo ma = gia tri toan hang lon nhat(toan hang cuoi) bao loi MS020095
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              } else if (
                Number(formMetricList[i].operatorValue) < minOperatorValue ||
                Number(formMetricList[i].operatorValue) > maxOperatorValue
              ) {
                // neu gia tri toan hang dong tiep theo ma < toan hang nho nhat hoac gia tri toan hang dong tiep theo ma > toan hang lon nhat thi bao loi MS020095
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              } else if (!['<', '<='].includes(formMetricList[i].operator?.value || '')) {
                // neu gia tri toan tu dong tiep khac '<', '<=' thi bao loi MS020095
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              }
            }
            previousOperatorValue = Number(formMetricList[i].operatorValue)
          }
        }
      } else if (minValIndex === formMetricList.length - 1) {
        // toan hang nho nhat o dong cuoi cung
        // rule toan hang nho nhat o dong cuoi cung tuong tu nhu rule toan hang o dong dau tien, chi khac la ta xet phan tu tu cuoi len toi phan tu dau tien cua mang
        if (!['<', '<='].includes(formMetricList[minValIndex].operator?.value || '')) {
          showToast(t('message:MS020095'), 'error')
          isValidFlag = false
        } else {
          previousOperatorValue = minOperatorValue
          for (let i = minValIndex - 1; i >= 0; i--) {
            if (i === 0) {
              if (Number(formMetricList[i].operatorValue) < 100) {
                if (!['>', '>='].includes(formMetricList[i].operator?.value || '')) {
                  showToast(t('message:MS020095'), 'error')
                  isValidFlag = false
                  break
                }
              } else if (!['<', '<='].includes(formMetricList[i].operator?.value || '')) {
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              }
            } else {
              if (Number(formMetricList[i].operatorValue) <= previousOperatorValue) {
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              } else if (
                formMetricList[i].operator?.value === '<=' &&
                Number(formMetricList[i].operatorValue) === maxOperatorValue
              ) {
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              } else if (!['<', '<='].includes(formMetricList[i].operator?.value || '')) {
                showToast(t('message:MS020095'), 'error')
                isValidFlag = false
                break
              }
            }
            previousOperatorValue = Number(formMetricList[i].operatorValue)
          }
        }
      }
    }
    return isValidFlag
  }

  const validateEditedMetricList = () => {
    let isValidFlag = true
    const formData = getValues()
    if (
      formData.editingMetricArr.some((x) => !x.operatorValue || !x.operatorValue.match(/^[0-9]+$/)) &&
      !isNumber16Group
    ) {
      showToast(t('message:MS020092'), 'error')
      isValidFlag = false
    } else if (
      formData.editingMetricArr.some((x) => !x.ragThreshold) ||
      formData.editingMetricArr.some((x) => !['0', '1', '2'].includes(x.ragThreshold))
    ) {
      showToast(t('message:MS020093'), 'error')
      isValidFlag = false
    } else if (
      formData.editingMetricArr.some((x) => !x.multiplier || !x.multiplier.match(/^[0-9]+$/)) ||
      checkDiffMultiplier(formData)
    ) {
      showToast(t('message:MS020094'), 'error')
      isValidFlag = false
    }
    return isValidFlag
  }

  const onSubmitEvent = async () => {
    const isValidForm = await trigger()
    if (isValidForm && validateEditedMetricList() && validateAC6Rule()) {
      const submitedData: ACViewMetricListService.ACViewMetricResponse[] = getSaveData()
      showGlobalLoading(true)
      pipe(
        ACViewMetricListService.saveEditedMetricList(submitedData),
        ZIO.map((responseData) => {
          showGlobalLoading(false)
          if (responseData.data && responseData.data.length > 0) {
            showToast(t('message:MS020097'), 'success')
            props.navigation.navigate('ACViewMetricList')
          } else {
            showToast(t('message:MS020096'), 'error')
          }
        }),
        ZIO.mapError((err) => {
          showGlobalLoading(false)
          showToast(t('message:MS020096'), 'error')
          return err
        }),
        ZIO.unsafeRun({})
      )
    }
  }

  return (
    <View style={ACSharedStyles.wrapper}>
      <ScrollView style={ACSharedStyles.container} horizontal={false}>
        <View style={ACSharedStyles.headingContainer}>
          <Text style={ACSharedStyles.headingText}>{t('AdministratorConfig:EditMetrics')}</Text>
        </View>
        <View style={editStyles.tooltipContainer}>
          <Tooltip style={{ cursor: 'pointer' }} arrow title={getTooltipContent()} placement="bottom-start">
            <View>
              <AntDesign name="questioncircleo" size={20} color="black" />
            </View>
          </Tooltip>
        </View>
        <HightLightTable
          displayedColumns={displayedColumns}
          dataSource={editingMetricList}
          tableContainerClassName={makeTableStyles().container}
          stickyHeader
        ></HightLightTable>
        <View style={[ACSharedStyles.buttonContainer]}>
          {userPermissions.includes(Permission.ResetEditMetricBSCAdminConfig) && (
            <TouchableOpacity style={[sharedStyle.button, ACSharedStyles.buttonContent]} onPress={() => onResetEvent()}>
              <Text style={sharedStyle.textButton}>{t('AdministratorConfig:Reset')}</Text>
            </TouchableOpacity>
          )}
          {userPermissions.includes(Permission.SubmitEditMetricBSCAdminConfig) && (
            <TouchableOpacity
              style={[sharedStyle.button, sharedStyle.btnRed, ACSharedStyles.buttonContent]}
              onPress={() => onSubmitEvent()}
            >
              <Text style={sharedStyle.textButtonRed}>{t('AdministratorConfig:Submit')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </View>
  )
}
const editStyles = StyleSheet.create({
  tooltipContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
  // headingText: {
  //   fontSize: 18,
  //   fontWeight: '500'
  // }
})
